import BusinessService from '@/business/modules/business/business.service';
import { ITEM_SIZE } from '@/presentation';

import type {
	IBusinessService,
	ISendSmsRequest,
	IRequestValidateBusiness,
	IRegisterBusinessRequest,
	IInquiryBusinessResponse,
	IParamsSearchOnBusiness,
	IAssignBusinessCategoriesRequest,
	IVerifyBusiness,
	ICreateCustomerRequest,
	ICustomerClubDashboardParams,
	IUpdateBusinessProfileRequest,
	IParamsGetBusinessesByCity,
	IRecordBusinessProfileContactRequest,
} from '@/business/modules/business/business.model';

type UserType = 'normal' | 'vip';
type GetCustomersParams = { page?: number; size?: number };

class BusinessController {
	static #instance: BusinessController;
	private readonly service: IBusinessService;

	private constructor() {
		this.service = BusinessService.getInstance();
	}

	public static getInstance(): BusinessController {
		if (!BusinessController.#instance) {
			BusinessController.#instance = new BusinessController();
		}
		return BusinessController.#instance;
	}

	inquiryBusiness(): Promise<IInquiryBusinessResponse> {
		return this.service.inquiryBusiness();
	}

	async singupAsBusiness(data: IRegisterBusinessRequest) {
		return await this.service.singupAsBusiness(data);
	}

	getBusinessInformation(id: string) {
		return this.service.getBusinessInformation(id);
	}

	getBusinessProfileBySlug(params: { citySlug: string; businessSlug: string }) {
		return this.service.getBusinessProfileBySlug(params.citySlug, params.businessSlug);
	}

	recordProfileContact(data: IRecordBusinessProfileContactRequest) {
		return this.service.recordProfileContact(data);
	}

	updataBusinessProfile(data: IUpdateBusinessProfileRequest) {
		return this.service.updataBusinessProfile(data);
	}

	requestValidateBusiness(data: IRequestValidateBusiness) {
		return this.service.requestValidateBusiness(data);
	}

	searchOnBusiness(params: IParamsSearchOnBusiness) {
		return this.service.searchOnBusiness(params);
	}

	getBusinessesByCity(params: IParamsGetBusinessesByCity) {
		return this.service.getBusinessesByCity(params);
	}

	sendSms(data: ISendSmsRequest) {
		return this.service.sendSms(data);
	}

	getCustomerClubDashboard(params?: ICustomerClubDashboardParams) {
		return this.service.getCustomerClubDashboard(params);
	}

	submitRating(data: object) {
		return this.service.submitRating(data);
	}

	makeUserVIP(userId) {
		return this.service.makeUserVIP(userId);
	}

	makeUserNormal(userId) {
		return this.service.makeUserNormal(userId);
	}

	createCustomer(data: ICreateCustomerRequest) {
		return this.service.createCustomer(data);
	}

	getCustomersByType(typeOfUser: UserType, params: GetCustomersParams = {}) {
		const serviceMethodByUserType: Record<UserType, keyof BusinessService> = {
			normal: 'getNormalUser',
			vip: 'getVIPUser',
		};
		const pagination = { page: params.page || 1, size: params.size || ITEM_SIZE };
		return (this.service[serviceMethodByUserType[typeOfUser]] as (params: { page: number; size: number }) => any)(pagination);
	}

	getPendingBusinesses(params) {
		return this.service.getPendingBusinesses(params);
	}

	assignCategoriesToBusiness(data: IAssignBusinessCategoriesRequest) {
		return this.service.assignCategoriesToBusiness(data);
	}

	getCategoriesOfBusiness(businessId: string) {
		return this.service.getCategoriesOfBusiness(businessId);
	}

	verifyBusiness(params: IVerifyBusiness) {
		return this.service.verifyBusiness(params);
	}

	updateReserveTimePayment(enabled: boolean) {
		return this.service.updateReserveTimePayment(enabled);
	}

	updateOnlineReservation(enabled: boolean) {
		return this.service.updateOnlineReservation(enabled);
	}

	updateCancellationPolicy(data) {
		return this.service.updateCancellationPolicy(data);
	}

	createSlotTime(data) {
		return this.service.createSlotTime(data);
	}
}

export default BusinessController;
